// /app/frontend/src/components/InstallationScheduler.jsx
// Installation scheduling component for post-checkout

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Calendar as CalendarIcon, Clock, MapPin, Loader2, Check, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_SLOTS = [
  { value: 'morning', label: 'Morning (8am - 12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm - 4pm)' },
  { value: 'evening', label: 'Evening (4pm - 7pm)' }
];

const InstallationScheduler = ({ 
  orderId, 
  token, 
  shippingAddress = {},
  onComplete,
  onSkip 
}) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [date1Open, setDate1Open] = useState(false);
  const [date2Open, setDate2Open] = useState(false);
  
  const [formData, setFormData] = useState({
    preferred_date_1: null,
    preferred_time_1: '',
    preferred_date_2: null,
    preferred_time_2: '',
    notes: '',
    address: {
      street: shippingAddress.street || '',
      city: shippingAddress.city || '',
      state: shippingAddress.state || '',
      zip: shippingAddress.zip || ''
    }
  });

  const [useShippingAddress, setUseShippingAddress] = useState(true);
  
  // Minimum date is tomorrow
  const minDate = addDays(startOfToday(), 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.preferred_date_1 || !formData.preferred_time_1) {
      toast.error('Please select your first preferred date and time');
      return;
    }
    if (!formData.preferred_date_2 || !formData.preferred_time_2) {
      toast.error('Please select your second preferred date and time');
      return;
    }
    
    // Check dates are different
    const date1Str = format(formData.preferred_date_1, 'yyyy-MM-dd');
    const date2Str = format(formData.preferred_date_2, 'yyyy-MM-dd');
    
    if (date1Str === date2Str && formData.preferred_time_1 === formData.preferred_time_2) {
      toast.error('Please select two different date/time options');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/user/installation-request`,
        {
          order_id: orderId,
          preferred_date_1: date1Str,
          preferred_time_1: formData.preferred_time_1,
          preferred_date_2: date2Str,
          preferred_time_2: formData.preferred_time_2,
          notes: formData.notes,
          address: useShippingAddress ? shippingAddress : formData.address
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSubmitted(true);
      toast.success('Installation request submitted!');
      
      if (onComplete) {
        setTimeout(() => onComplete(), 2000);
      }
    } catch (error) {
      console.error('Failed to submit installation request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-green-50 border border-green-200 rounded-xl p-8 text-center"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-green-800 mb-2">
          Installation Request Submitted!
        </h3>
        <p className="text-green-700 mb-4">
          We'll review your preferred dates and confirm your installation appointment soon.
        </p>
        <p className="text-sm text-green-600">
          You'll receive an email once your installation is confirmed.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-neutral-200 rounded-xl overflow-hidden"
    >
      <div className="p-4 border-b border-neutral-200 bg-amber-50">
        <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-amber-600" />
          Schedule Professional Installation
        </h3>
        <p className="text-sm text-neutral-600 mt-1">
          Our experts will install your shades. Select two preferred appointment times.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Info Alert */}
        <div className="flex gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Please provide two different date options</p>
            <p className="text-blue-600">This helps us confirm your preferred time or offer an alternative.</p>
          </div>
        </div>

        {/* First Preferred Date/Time */}
        <div className="space-y-4">
          <h4 className="font-medium text-neutral-900">First Choice</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Preferred Date *</Label>
              <Popover open={date1Open} onOpenChange={setDate1Open}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full mt-1 justify-start text-left font-normal",
                      !formData.preferred_date_1 && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.preferred_date_1 ? (
                      format(formData.preferred_date_1, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.preferred_date_1}
                    onSelect={(date) => {
                      setFormData(prev => ({ ...prev, preferred_date_1: date }));
                      setDate1Open(false);
                    }}
                    disabled={(date) => isBefore(date, minDate)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Preferred Time *</Label>
              <Select
                value={formData.preferred_time_1}
                onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_time_1: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time slot" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map(slot => (
                    <SelectItem key={slot.value} value={slot.value}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Second Preferred Date/Time */}
        <div className="space-y-4">
          <h4 className="font-medium text-neutral-900">Second Choice (Backup)</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Preferred Date *</Label>
              <Popover open={date2Open} onOpenChange={setDate2Open}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full mt-1 justify-start text-left font-normal",
                      !formData.preferred_date_2 && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.preferred_date_2 ? (
                      format(formData.preferred_date_2, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.preferred_date_2}
                    onSelect={(date) => {
                      setFormData(prev => ({ ...prev, preferred_date_2: date }));
                      setDate2Open(false);
                    }}
                    disabled={(date) => isBefore(date, minDate)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Preferred Time *</Label>
              <Select
                value={formData.preferred_time_2}
                onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_time_2: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select time slot" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map(slot => (
                    <SelectItem key={slot.value} value={slot.value}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Installation Address */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-neutral-900 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-600" />
              Installation Address
            </h4>
            {shippingAddress.street && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={useShippingAddress}
                  onChange={(e) => setUseShippingAddress(e.target.checked)}
                  className="rounded"
                />
                Use shipping address
              </label>
            )}
          </div>
          
          {!useShippingAddress && (
            <div className="space-y-3">
              <Input
                placeholder="Street Address"
                value={formData.address.street}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  address: { ...prev.address, street: e.target.value }
                }))}
              />
              <div className="grid grid-cols-3 gap-3">
                <Input
                  placeholder="City"
                  value={formData.address.city}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value }
                  }))}
                />
                <Input
                  placeholder="State"
                  value={formData.address.state}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, state: e.target.value }
                  }))}
                />
                <Input
                  placeholder="ZIP"
                  value={formData.address.zip}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, zip: e.target.value }
                  }))}
                />
              </div>
            </div>
          )}
          
          {useShippingAddress && shippingAddress.street && (
            <p className="text-sm text-neutral-500 bg-neutral-50 p-3 rounded-lg">
              {shippingAddress.street}, {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <Label>Special Instructions (Optional)</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Gate code, parking instructions, pets, etc."
            rows={3}
            className="mt-1"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-neutral-200">
          <Button
            type="button"
            variant="outline"
            onClick={onSkip}
            className="flex-1"
          >
            Skip for Now
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1 bg-amber-600 hover:bg-amber-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Request Installation
              </>
            )}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default InstallationScheduler;
